/**
 * Hook de la biblioteca de medios DOOH (TG-58) — estado de la vista separado de la
 * presentación. La red vive en `doohApi`; aquí solo loading/error/refresh.
 */
import { useCallback, useEffect, useState } from 'react';
import type { DoohMedia } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { doohApi } from '../api/doohApi';

export interface UseDoohMediaResult {
  data: DoohMedia[];
  loading: boolean;
  error: string | null;
  /** Recarga la biblioteca (tras subir/eliminar un medio o al reintentar). */
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar los medios.';
}

export function useDoohMedia(): UseDoohMediaResult {
  const [data, setData] = useState<DoohMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    doohApi
      .listMedia()
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, loading, error, refresh };
}
